// Spectrum maths for the River visualiser: FFT bins (dBFS, from an AnalyserNode) → log-spaced
// bands → 0–1 levels, with attack/release and falling peak caps. Pure, so it is unit-tested.

export const F_LOW = 30;
export const F_HIGH = 16_000;
/** Level 0 at this many dB (per band, after tilt), 1 at `DB_TOP`. */
export const DB_FLOOR = -80;
export const DB_TOP = -27;
/** Pink-noise tilt: +3 dB per octave around 1 kHz, so a balanced mix looks level across bands. */
export const TILT_DB_PER_OCT = 3;

export interface Bands {
  /** First bin (inclusive) and last bin (exclusive) of each band. */
  lo: Int32Array;
  hi: Int32Array;
  /** Tilt (dB) of each band, at its centre frequency. */
  tilt: Float32Array;
}

/** `n` log-spaced bands between F_LOW and F_HIGH; every band gets at least one bin. */
export function makeBands(n: number, sampleRate: number, fftSize: number): Bands {
  const binHz = sampleRate / fftSize;
  const bins = fftSize / 2;
  const lo = new Int32Array(n);
  const hi = new Int32Array(n);
  const tilt = new Float32Array(n);
  const ratio = F_HIGH / F_LOW;
  for (let i = 0; i < n; i++) {
    const f0 = F_LOW * ratio ** (i / n);
    const f1 = F_LOW * ratio ** ((i + 1) / n);
    let a = Math.round(f0 / binHz);
    let b = Math.round(f1 / binHz);
    a = Math.min(Math.max(a, 1), bins - 1);
    b = Math.min(Math.max(b, a + 1), bins);
    lo[i] = a;
    hi[i] = b;
    tilt[i] = TILT_DB_PER_OCT * Math.log2(Math.sqrt(f0 * f1) / 1000);
  }
  return { lo, hi, tilt };
}

/** Band levels 0–1 from FFT magnitudes in dB: the mean power of each band's bins, tilted. */
export function bandLevels(db: Float32Array, bands: Bands, out: Float32Array): void {
  for (let i = 0; i < out.length; i++) {
    let p = 0;
    for (let k = bands.lo[i]; k < bands.hi[i]; k++) {
      const v = db[k];
      if (v > -200) p += 10 ** (v / 10);
    }
    p /= bands.hi[i] - bands.lo[i];
    const level = p > 0 ? 10 * Math.log10(p) + bands.tilt[i] : -Infinity;
    out[i] = Math.min(1, Math.max(0, (level - DB_FLOOR) / (DB_TOP - DB_FLOOR)));
  }
}

export interface Meter {
  level: Float32Array;
  peak: Float32Array;
  hold: Float32Array;
}

export const newMeter = (n: number): Meter => ({
  level: new Float32Array(n),
  peak: new Float32Array(n),
  hold: new Float32Array(n),
});

/** Release time constant (s), peak hold (s) and peak fall speed (levels/s). */
export const RELEASE_S = 0.12;
export const HOLD_S = 0.35;
export const PEAK_FALL = 1.1;

/** One frame of meter ballistics: instant attack, exponential release, held then falling peaks. */
export function stepMeter(m: Meter, target: Float32Array, dt: number): void {
  const rel = Math.exp(-dt / RELEASE_S);
  for (let i = 0; i < target.length; i++) {
    const t = target[i];
    m.level[i] = t >= m.level[i] ? t : t + (m.level[i] - t) * rel;
    if (m.level[i] < 1e-3) m.level[i] = 0; // settle to exactly dark
    if (m.level[i] >= m.peak[i]) {
      m.peak[i] = m.level[i];
      m.hold[i] = HOLD_S;
    } else if (m.hold[i] > 0) {
      m.hold[i] -= dt;
    } else {
      m.peak[i] = Math.max(m.level[i], m.peak[i] - PEAK_FALL * dt);
    }
  }
}
